import { useEffect, useRef, useState } from 'react';
import { btp } from '../i18n';
import { FieldRow } from '../inspector/FieldRow';
import { HsvColorPicker } from '../inspector/controls/HsvColorPicker';

/**
 * A PURE-HEX color control for the global theme editor (feature 0019 Slice 2).
 *
 * Deliberately NOT the inspector `ColorControl`: that control edits a `ColorValue` and offers the theme
 * TOKEN swatches (`{ref:'theme',token}`). But these ARE the theme's source colors — the model stores a
 * raw hex string, and offering an "accent" swatch inside the "Accent 1" picker would be self-referential
 * (and unstorable — `applyThemeVars` emits the raw hex, not a `var()`). So this reuses the exact same
 * chrome (swatch + hex field + HSV popover, `bld-color__*` classes) but drops the token grid: the seller
 * always picks a concrete color, which is what a theme SOURCE color must be.
 */
const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function ThemeColorControl({
  id,
  label,
  info,
  value,
  onChange,
}: {
  id: string;
  label: string;
  info?: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [hex, setHex] = useState(value);

  // Follow external changes (undo/redo, load) without clobbering the seller's mid-type value.
  useEffect(() => setHex(value), [value]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Commit only a well-formed hex (an in-progress "#1" keystroke updates the field but not the theme).
  const commit = (v: string) => {
    setHex(v);
    if (HEX_RE.test(v)) onChange(v);
  };

  const swatch = HEX_RE.test(hex) ? hex : value;
  const pickerHex = /^#[0-9a-f]{6}$/i.test(hex) ? hex : value;

  return (
    <FieldRow id={id} label={label} info={info} labelAs="span" layout="row">
      <div className="bld-color" ref={ref}>
        <div className="bld-color__box">
          <button
            type="button"
            className="bld-color__chipbtn"
            aria-label={btp('chrome.color.picker_aria', '{name} color picker', { name: label })}
            aria-haspopup="dialog"
            aria-expanded={open}
            style={{ background: swatch }}
            onClick={() => setOpen((v) => !v)}
          />
          <input
            className="bld-color__hexin"
            type="text"
            aria-label={btp('chrome.color.hex_aria', '{name} hex value', { name: label })}
            value={hex}
            placeholder={value}
            onChange={(e) => commit(e.target.value)}
          />
        </div>

        {open && (
          <div
            className="bld-color__pop"
            role="dialog"
            aria-label={btp('chrome.color.pop_aria', '{name} picker', { name: label })}
          >
            <HsvColorPicker
              value={pickerHex}
              onChange={(h) => commit(h)}
              hexLabel={btp('chrome.color.pop_hex_aria', '{name} picker hex', { name: label })}
            />
          </div>
        )}
      </div>
    </FieldRow>
  );
}
